import { useCallback, useEffect, useEffectEvent, useMemo, useReducer, useState } from 'react';
import type { AllocationDraft } from '../fields/allocation.js';
import { hasSelectField, type CheckoutIo, type CheckoutRules } from '../model/checkout_env.js';
import type {
  CheckoutBookingDraft,
  CheckoutBookingForm,
  CheckoutBudget,
  CheckoutField,
  CheckoutPrefill,
  CheckoutProductType,
  CheckoutSubmitBlocker,
  CheckoutTravelerDraft,
  CheckoutTravelerRef,
} from '../model/checkout_source.js';
import { allocationFromPrefill, filterPrefillEdits } from '../model/prefill_seed.js';
import type { CheckoutAllocationOwner } from '../model/product_contract.js';
import {
  seedAllocation,
  seedTravelerDraft,
  travelerNeedsEditing,
} from '../model/seed_traveler_form.js';
import { useBudgetAdvisory } from './use_budget_advisory.js';
import { useKeyedValue } from '../../shared/use_keyed_value.js';

type Phase = 'loading' | 'ready' | 'error';

/** Per-traveler slice — the traveler ref, its own fetched corporate form, and
 *  the drafts collected for it (allocation + identity). `phase` tracks that one
 *  traveler's form fetch. */
interface PerTravelerState {
  traveler: CheckoutTravelerRef;
  phase: Phase;
  form?: CheckoutBookingForm;
  /** As linhas do rateio: uma, ou até 10 com a flag (#6697). */
  allocations: AllocationDraft[];
  travelerDraft: CheckoutTravelerDraft;
  travelerEditing: boolean;
}

interface State {
  travelers: PerTravelerState[];
  // Booking-level (shared across travelers — collected once).
  motiveId: string;
  justificationId?: string;
  observation: string;
  policyTravelerId?: string;
  /** Saldo de cada linha do rateio do bloco que paga sozinho; advisory, nunca trava. */
  budgets?: readonly (CheckoutBudget | undefined)[];
  /** Prefill da 2ª compra da mesma trip (guardado no INIT para o FORM_OK ler,
   *  por-viajante, quando cada form fresco chega). */
  prefill?: CheckoutPrefill;
}

type Action =
  | {
      type: 'INIT';
      travelers: CheckoutTravelerRef[];
      policyTravelerId?: string;
      draft?: CheckoutBookingDraft;
      prefill?: CheckoutPrefill;
    }
  | { type: 'FORM_OK'; index: number; form: CheckoutBookingForm; fields: CheckoutField[] }
  | { type: 'FORM_ERROR'; index: number }
  | { type: 'SET_TRAVELER_EDITING'; index: number; value: boolean }
  | { type: 'UPDATE_TRAVELER'; index: number; patch: CheckoutTravelerDraft }
  | { type: 'UPDATE_ALLOCATIONS'; index: number; value: AllocationDraft[] }
  | { type: 'SET_MOTIVE'; value: string }
  | { type: 'SET_JUSTIFICATION'; value: string | undefined }
  | { type: 'SET_OBSERVATION'; value: string }
  | { type: 'BUDGET_OK'; budgets: readonly (CheckoutBudget | undefined)[] }
  | { type: 'BUDGET_CLEAR' };

const emptyAllocation: AllocationDraft = { companyId: '', costCenterId: '', projectId: '' };

function loadingTraveler(traveler: CheckoutTravelerRef): PerTravelerState {
  return {
    traveler,
    phase: 'loading',
    allocations: [emptyAllocation],
    travelerDraft: {},
    travelerEditing: false,
  };
}

/** Replaces the slice at `index` via `patch`, leaving the rest untouched. */
function patchTraveler(
  state: State,
  index: number,
  patch: (slice: PerTravelerState) => PerTravelerState,
): State {
  return {
    ...state,
    travelers: state.travelers.map((slice, i) => (i === index ? patch(slice) : slice)),
  };
}

/** Precedência dos campos de RESERVA no INIT: `draft` (reopen da MESMA
 *  cotação — CC-4) vence `prefill` (2ª compra da MESMA trip) — um rascunho
 *  vivo é mais recente que o snapshot. Extraído do `reducer` para manter sua
 *  complexidade sob o teto (o cálculo em si não ramifica por `action.type`). */
function resolveInitialBookingFields(
  draft: CheckoutBookingDraft | undefined,
  prefill: CheckoutPrefill | undefined,
): Pick<State, 'motiveId' | 'justificationId' | 'observation'> {
  return {
    motiveId: draft?.motiveId ?? prefill?.motiveId ?? '',
    justificationId: draft?.justificationId ?? prefill?.justificationId,
    observation: draft?.observation ?? prefill?.observation ?? '',
  };
}

const BOOKING_BLOCKERS: readonly CheckoutSubmitBlocker[] = [
  'allocation',
  'project',
  'motive',
  'justification',
];

/** União sem repetição das pendências de cada viajante, mantendo a ordem da
 *  tela: identidade dos cartões primeiro, depois rateio, motivo e justificativa
 *  (o motivo é um só, mas cada viajante o reporta). */
function mergeBlockers(perTraveler: CheckoutSubmitBlocker[]): CheckoutSubmitBlocker[] {
  const unique = new Set(perTraveler);
  return [
    ...[...unique].filter((blocker) => !BOOKING_BLOCKERS.includes(blocker)),
    ...BOOKING_BLOCKERS.filter((blocker) => unique.has(blocker)),
  ];
}

/** Pendências de cada viajante (o motivo/justificativa compartilhados entram
 *  por todos), unidas por `mergeBlockers`. */
function blockersOf(
  state: State,
  opts: {
    rules: CheckoutRules;
    fields: CheckoutField[];
    withinPolicy: boolean;
    ownsAllocation: (slice: PerTravelerState) => boolean;
  },
): CheckoutSubmitBlocker[] {
  // Both screens render the justification select from the first traveler's
  // catalog (`firstForm` / `catalog`), so the gate reads the same one.
  const justificationSelectable = (state.travelers[0]?.form?.justifications.length ?? 0) > 0;
  return mergeBlockers(
    state.travelers.flatMap((slice) =>
      opts.rules.submitBlockers({
        fields: opts.fields,
        withinPolicy: opts.withinPolicy,
        enableProjectField: slice.form?.enableProjectField ?? false,
        allocations: slice.allocations,
        allowManyAllocations: slice.form?.allowManyAllocations === true,
        motiveId: state.motiveId,
        justificationId: state.justificationId,
        missingRequired: slice.form?.traveler?.missingRequired ?? [],
        travelerDraft: slice.travelerDraft,
        allocationRequired: opts.ownsAllocation(slice),
        justificationSelectable,
      }),
    ),
  );
}

/** As ações que endereçam UM viajante — as que carregam `index`. */
type TravelerAction = Extract<Action, { index: number }>;

/** Patches da fatia por-viajante (identidade/alocação/fase daquele bloco). */
function travelerReducer(state: State, action: TravelerAction): State {
  switch (action.type) {
    case 'FORM_OK': {
      const next = patchTraveler(state, action.index, (slice) => {
        const prefillFor = state.prefill?.travelers?.find((p) => p.id === slice.traveler.id);
        const prefillAllocation = allocationFromPrefill(action.form, prefillFor?.allocation);
        // Os `edits` do prefill entram só onde o PERFIL FRESCO tem buraco
        // (`filterPrefillEdits`): um passaporte renovado depois da última
        // compra não pode ser sobrescrito, calado, pelo documento velho.
        const travelerDraft = {
          ...seedTravelerDraft(action.form),
          ...filterPrefillEdits(prefillFor?.edits, action.form),
        };
        return {
          ...slice,
          phase: 'ready',
          form: action.form,
          // O prefill da 2ª compra traz uma linha só (a 1ª da compra anterior).
          allocations: [prefillAllocation ?? seedAllocation(action.form)],
          travelerDraft,
          travelerEditing: travelerNeedsEditing(action.form, action.fields, travelerDraft),
        };
      });
      // FORM_OK só dispara na carga (pré-interação): resetar motivo/justificativa
      // cujo id não existe no form protege igualmente prefill E draft (CC-4) stale.
      return {
        ...next,
        motiveId:
          next.motiveId && action.form.motives.some((m) => m.id === next.motiveId)
            ? next.motiveId
            : '',
        justificationId:
          next.justificationId &&
          action.form.justifications.some((j) => j.id === next.justificationId)
            ? next.justificationId
            : undefined,
      };
    }
    case 'FORM_ERROR':
      return patchTraveler(state, action.index, (slice) => ({ ...slice, phase: 'error' }));
    case 'SET_TRAVELER_EDITING':
      return patchTraveler(state, action.index, (slice) => ({
        ...slice,
        travelerEditing: action.value,
      }));
    case 'UPDATE_TRAVELER':
      return patchTraveler(state, action.index, (slice) => ({
        ...slice,
        travelerDraft: { ...slice.travelerDraft, ...action.patch },
      }));
    case 'UPDATE_ALLOCATIONS':
      return patchTraveler(state, action.index, (slice) => ({
        ...slice,
        allocations: action.value,
      }));
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/** Estado de nível de reserva; as ações por-viajante, listadas, caem no `default`, que as
 *  delega (o discriminante já saiu estreitado — um `type` novo sem `index` não compila). */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'INIT':
      return {
        travelers: action.travelers.map(loadingTraveler),
        ...resolveInitialBookingFields(action.draft, action.prefill),
        policyTravelerId: action.policyTravelerId,
        prefill: action.prefill,
      };
    case 'SET_MOTIVE':
      return { ...state, motiveId: action.value };
    case 'SET_JUSTIFICATION':
      return { ...state, justificationId: action.value };
    case 'SET_OBSERVATION':
      return { ...state, observation: action.value };
    case 'BUDGET_OK':
      return { ...state, budgets: action.budgets };
    case 'BUDGET_CLEAR':
      return { ...state, budgets: undefined };
    case 'UPDATE_TRAVELER':
    case 'SET_TRAVELER_EDITING':
    case 'UPDATE_ALLOCATIONS':
    case 'FORM_OK':
    case 'FORM_ERROR':
    default:
      return travelerReducer(state, action);
  }
}

const initialState: State = {
  travelers: [],
  motiveId: '',
  observation: '',
};

/** Resolves the initial policy traveler against the actual set: the wire hint if
 *  it names a real traveler, else the requester if present, else the first
 *  traveler. Guarantees a governing traveler even when the hint is stale. */
function resolveInitialPolicy(
  travelers: CheckoutTravelerRef[],
  policyTravelerId: string | undefined,
  requesterId: string | undefined,
): string | undefined {
  const ids = new Set(travelers.map((t) => t.id));
  if (policyTravelerId && ids.has(policyTravelerId)) return policyTravelerId;
  if (requesterId && ids.has(requesterId)) return requesterId;
  return travelers[0]?.id;
}

/** Dono do rateio e a alocação dele — só no modo `'requester'` (fora dele todo
 *  bloco aloca e não há dono único). */
function allocationOwnerOf(
  rules: CheckoutRules,
  state: State,
  allocationOwner: CheckoutAllocationOwner,
  requesterId: string | undefined,
): string | undefined {
  if (allocationOwner !== 'requester') return undefined;
  return rules.allocationOwnerId(
    state.travelers.map((slice) => slice.traveler.id),
    requesterId,
    state.policyTravelerId,
  );
}

/** Os quartos do modo `'requester'`, cada um com o titular na frente (#5522):
 *  os titulares são os blocos que alocam. Fora dele, nenhum. */
function roomsOf(
  rules: CheckoutRules,
  state: State,
  ownerId: string | undefined,
  guestsPerRoom: number | undefined,
): string[][] {
  if (!ownerId) return [];
  return rules.hotelRooms(
    state.travelers.map((slice) => slice.traveler.id),
    ownerId,
    guestsPerRoom,
  );
}

/** O bloco que paga sozinho: o titular único no modo `'requester'`, ou o único
 *  viajante quando cada um aloca. Com vários pagando, nenhum. */
function budgetSliceOf(
  state: State,
  allocationOwner: CheckoutAllocationOwner,
  holderIds: readonly string[],
): PerTravelerState | undefined {
  if (allocationOwner === 'requester') {
    const [only, ...others] = holderIds;
    return others.length === 0
      ? state.travelers.find((slice) => slice.traveler.id === only)
      : undefined;
  }
  const [only, ...others] = state.travelers;
  return others.length === 0 ? only : undefined;
}

/** O saldo do rateio no resultado: `budget` com uma linha só (o farol de
 *  sempre, e o card 1click), `budgets` sempre que houver consulta. */
function budgetFields(
  budgets: State['budgets'],
): Pick<UseMultiTravelerCheckoutFormResult, 'budget' | 'budgets'> {
  if (!budgets) return {};
  const [only] = budgets;
  return budgets.length === 1 && only ? { budget: only, budgets } : { budgets };
}

export interface UseMultiTravelerCheckoutFormOptions {
  /** The host's reads; `null` ⇒ every traveler's form fails (fail-closed). A new
   *  identity reruns the load (see `CheckoutIo`). */
  io: CheckoutIo | null;
  rules: CheckoutRules;
  productType: CheckoutProductType;
  fields: CheckoutField[];
  open: boolean;
  travelers: CheckoutTravelerRef[];
  requesterId?: string;
  policyTravelerId?: string;
  withinPolicy: boolean;
  /** Rascunho de reserva de uma abertura anterior da MESMA cotação (CC-4): o
   *  INIT re-seeda Motivo/Justificativa/Observação em vez de zerá-los. */
  initialDraft?: CheckoutBookingDraft;
  /** Prefill da 2ª compra da MESMA trip (spec 2026-08-28): o INIT semeia
   *  Motivo/Justificativa/Observação (perdendo para `initialDraft`) e o
   *  FORM_OK semeia allocation/edits por-viajante, VALIDADOS contra o form
   *  fresco (fail-to-seed, nunca fail-to-submit — ver `allocationFromPrefill`). */
  prefill?: CheckoutPrefill;
  /** `'requester'` (multi de hotel): só o bloco do dono do rateio precisa de
   *  alocação — o requester, ou o hóspede da política numa reserva só para
   *  terceiros (`allocationOwnerId`). Default `'every-traveler'` (voo: todo
   *  bloco aloca). */
  allocationOwner?: CheckoutAllocationOwner;
  /** Hóspedes por quarto (hotel): no modo `'requester'`, cada titular de quarto
   *  aloca (#5522). Ausente, só o dono do rateio. */
  guestsPerRoom?: number;
  /** Total product-aware do carrinho — entra na consulta de saldo do advisory
   *  (só no modo `'requester'`). */
  total: number;
  /** Início da estadia/viagem para a consulta de saldo; ausente ⇒ hoje. */
  startDate?: string;
}

export interface UseMultiTravelerCheckoutFormResult {
  phase: Phase;
  travelers: PerTravelerState[];
  motiveId: string;
  justificationId?: string;
  observation: string;
  policyTravelerId?: string;
  /** Bloco que carrega o rateio no modo `'requester'` (`allocationOwnerId`). */
  allocationOwnerId?: string;
  /** Os quartos do modo `'requester'`, cada um com o titular na frente: os
   *  titulares alocam, os acompanhantes não (#5522). Vazio fora dele. */
  rooms: string[][];
  /** O bloco cujo saldo o form consulta: o dono no modo `'requester'`, ou o
   *  único viajante quando cada um aloca. Com vários blocos alocando, nenhum. */
  budgetTravelerId?: string;
  /** Saldo do rateio de `budgetTravelerId` com uma linha só. */
  budget?: CheckoutBudget;
  /** Saldo de cada linha do rateio de `budgetTravelerId`, na ordem das linhas
   *  (cada uma consultada por preço × percentual, #6697). */
  budgets?: readonly (CheckoutBudget | undefined)[];
  canSubmit: boolean;
  /** Pendências de TODOS os viajantes, sem repetição, na ordem da tela. */
  submitBlockers: CheckoutSubmitBlocker[];
  setTravelerEditing: (index: number, value: boolean) => void;
  updateTraveler: (index: number, patch: CheckoutTravelerDraft) => void;
  updateAllocations: (index: number, value: AllocationDraft[]) => void;
  setMotiveId: (value: string) => void;
  setJustificationId: (value: string | undefined) => void;
  setObservation: (value: string) => void;
  /** Busca de novo o form de cada viajante (#5670): o 1click oferece «Tentar de
   *  novo» quando a primeira carga falha, sem mandar o viajante à tela cheia. */
  reload: () => void;
}

/** Checkout form state, one slice per traveler. Fetches each traveler's own
 *  corporate form via `port.getBookingForm(productType, travelerId)`, holds a
 *  slice per traveler (allocation + identity drafts) plus the booking-level
 *  motive/justification/observation collected once, and derives `canSubmit` by
 *  running `computeSubmitBlockers` per traveler (which ANDs the shared motive in).
 *
 *  O advisory de orçamento é consultado SÓ no modo `'requester'` (bancada de
 *  hotel), a partir do rateio do dono: com todo bloco alocando seriam N
 *  faróis, decisão de produto ainda não tomada. */
export function useMultiTravelerCheckoutForm({
  io: port,
  rules,
  productType,
  fields,
  open,
  travelers,
  requesterId,
  policyTravelerId,
  withinPolicy,
  initialDraft,
  prefill,
  allocationOwner = 'every-traveler',
  guestsPerRoom,
  total,
  startDate,
}: UseMultiTravelerCheckoutFormOptions): UseMultiTravelerCheckoutFormResult {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Cada «Tentar de novo» incrementa: o efeito de carga roda de novo (INIT + forms).
  const [attempt, setAttempt] = useState(0);
  // Evento, não dep do efeito: o rascunho/prefill (e os viajantes) são lidos só
  // quando o INIT roda; a identidade do objeto vinda do host não deve re-disparar
  // o refetch dos forms.
  const initInputs = useEffectEvent(() => ({ travelers, initialDraft, prefill }));
  // Mesma regra para o manifesto: lido só no FORM_OK (editor abre sem telefone).
  const readFields = useEffectEvent(() => fields);
  // O pedido de carga: o conjunto real de viajantes (os ids, não a identidade do
  // array) e a tentativa — um «Tentar de novo» é um pedido novo.
  const request = useKeyedValue({ travelerIds: travelers.map((t) => t.id), attempt });

  useEffect(() => {
    if (!open) return undefined;
    const { travelerIds } = request;
    const { travelers, initialDraft, prefill } = initInputs();
    dispatch({
      type: 'INIT',
      travelers,
      policyTravelerId: resolveInitialPolicy(travelers, policyTravelerId, requesterId),
      ...(initialDraft ? { draft: initialDraft } : {}),
      ...(prefill ? { prefill } : {}),
    });
    if (!port) {
      travelerIds.forEach((_, index) => {
        dispatch({ type: 'FORM_ERROR', index });
      });
      return undefined;
    }
    let cancelled = false;
    travelerIds.forEach((travelerId, index) => {
      port
        .getBookingForm(productType, travelerId)
        .then((form) => {
          if (cancelled) return;
          if (form.traveler?.id !== travelerId) {
            dispatch({ type: 'FORM_ERROR', index });
            return;
          }
          dispatch({ type: 'FORM_OK', index, form, fields: readFields() });
        })
        .catch(() => {
          if (!cancelled) dispatch({ type: 'FORM_ERROR', index });
        });
    });
    return () => {
      cancelled = true;
    };
  }, [open, productType, port, request, requesterId, policyTravelerId]);

  const phase = useMemo<Phase>(() => {
    if (state.travelers.length === 0) return 'loading';
    if (state.travelers.some((t) => t.phase === 'error')) return 'error';
    return state.travelers.every((t) => t.phase === 'ready') ? 'ready' : 'loading';
  }, [state.travelers]);

  // Fonte do advisory: o bloco que paga sozinho — o DONO no modo
  // `'requester'` (não `travelers[0]`, que pode ser um acompanhante) ou o
  // único viajante quando cada um aloca. Com vários pagando, cada tela consulta
  // os seus (o card 1-Click, por bloco) ou nenhum (a tela cheia).
  const ownerId = allocationOwnerOf(rules, state, allocationOwner, requesterId);
  const rooms = roomsOf(rules, state, ownerId, guestsPerRoom);
  const holderIdsKey = rooms.map((room) => room[0]).join(',');
  const holderIds = useMemo(() => (holderIdsKey ? holderIdsKey.split(',') : []), [holderIdsKey]);
  const budgetSlice = budgetSliceOf(state, allocationOwner, holderIds);
  const ownsAllocation = useCallback(
    (slice: PerTravelerState) =>
      allocationOwner !== 'requester' || holderIds.includes(slice.traveler.id),
    [allocationOwner, holderIds],
  );
  const onBudget = useCallback((budgets: readonly (CheckoutBudget | undefined)[]) => {
    dispatch({ type: 'BUDGET_OK', budgets });
  }, []);
  const onBudgetClear = useCallback(() => {
    dispatch({ type: 'BUDGET_CLEAR' });
  }, []);

  useBudgetAdvisory({
    io: port,
    enabled: open && phase === 'ready' && budgetSlice !== undefined,
    productType,
    rows: budgetSlice?.allocations ?? [],
    total,
    startDate,
    onBudget,
    onClear: onBudgetClear,
  });

  const submitBlockers = useMemo<CheckoutSubmitBlocker[]>(
    () =>
      phase === 'ready' ? blockersOf(state, { rules, fields, withinPolicy, ownsAllocation }) : [],
    [phase, state, rules, fields, withinPolicy, ownsAllocation],
  );

  // Multi de hotel sem nenhum bloco dono do rateio: não há de onde tirar a
  // alocação — trava aqui, mesma cerca fail-closed do builder.
  const ownerMissing = allocationOwner === 'requester' && ownerId === undefined;
  const canSubmit = phase === 'ready' && !ownerMissing && submitBlockers.length === 0;

  const setTravelerEditing = useCallback((index: number, value: boolean) => {
    dispatch({ type: 'SET_TRAVELER_EDITING', index, value });
  }, []);
  const updateTraveler = useCallback((index: number, patch: CheckoutTravelerDraft) => {
    dispatch({ type: 'UPDATE_TRAVELER', index, patch });
  }, []);
  const updateAllocations = useCallback((index: number, value: AllocationDraft[]) => {
    dispatch({ type: 'UPDATE_ALLOCATIONS', index, value });
  }, []);
  const setMotiveId = useCallback((value: string) => {
    dispatch({ type: 'SET_MOTIVE', value });
  }, []);
  const setJustificationId = useCallback((value: string | undefined) => {
    dispatch({ type: 'SET_JUSTIFICATION', value });
  }, []);
  const setObservation = useCallback((value: string) => {
    dispatch({ type: 'SET_OBSERVATION', value });
  }, []);
  const reload = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);

  return {
    phase,
    travelers: state.travelers,
    motiveId: state.motiveId,
    // Sem o campo no manifesto, uma justificativa herdada (rascunho ou
    // prefill) não sai: o envio levaria um valor que a tela não mostrou.
    ...(hasSelectField(fields, 'justification') && state.justificationId
      ? { justificationId: state.justificationId }
      : {}),
    observation: state.observation,
    policyTravelerId: state.policyTravelerId,
    ...(ownerId ? { allocationOwnerId: ownerId } : {}),
    rooms,
    ...(budgetSlice ? { budgetTravelerId: budgetSlice.traveler.id } : {}),
    ...budgetFields(state.budgets),
    canSubmit,
    submitBlockers,
    setTravelerEditing,
    updateTraveler,
    updateAllocations,
    setMotiveId,
    setJustificationId,
    setObservation,
    reload,
  };
}
